import { z } from 'zod';

export const zEnvBoolean = z
  .enum(['0', '1', 'true', 'false'])
  .catch('false')
  .transform((value) => value.trim() === 'true' || value.trim() === '1');

export interface ZodCompat<Output> {
  _output: Output;
  safeParse(data: unknown): { success: boolean; data?: Output; error?: unknown };
}

type ZodRuntimeType = z.ZodTypeAny & { _def?: { type?: string; typeName?: string } };

export function isZodType<Z extends z.ZodTypeAny>(value: unknown, matchType?: Z): value is z.ZodTypeAny {
  if (!value) return false;
  if (typeof value !== 'object') return false;
  if (typeof (value as { safeParse?: unknown }).safeParse !== 'function') return false;
  if (!matchType) return true;
  return zodTypeName(value as z.ZodTypeAny) === zodTypeName(matchType);
}

export function isZodError(value: unknown): value is z.ZodError {
  return value instanceof z.ZodError;
}

export function zodTypeName(type: z.ZodTypeAny): string {
  if (!isZodType(type)) throw new Error(`Invalid Zod type: ${type}`);
  const runtimeType = type as ZodRuntimeType;
  return runtimeType._def?.type ?? runtimeType._def?.typeName ?? runtimeType.constructor.name;
}

export function zodSingleOrArray<Z extends z.ZodTypeAny>(type: Z): z.ZodUnion<[z.ZodArray<Z>, Z]> {
  return z.array(type).or(type);
}

export const zodStringIsoDate = z
  .string()
  .transform((value) => {
    const v = z.iso.date().parse(value);
    return z.date().parse(v);
  })
  .describe('ISO date');
